import React, { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ContactSearchDropdown from "../common/ContactSearchDropdown";

const EMPTY_INITIAL_VALUES = {};
const EMPTY_EXISTING_DATA = [];

const buildInitialFormData = (fields, initialValues = EMPTY_INITIAL_VALUES) =>
  fields.reduce((acc, field) => {
    if (initialValues[field.key] !== undefined && initialValues[field.key] !== null) {
      acc[field.key] = initialValues[field.key];
    } else if (field.type === "file") {
      acc[field.key] = null;
    } else if (field.type === "checkbox") {
      acc[field.key] = false;
    } else {
      acc[field.key] = "";
    }
    return acc;
  }, {});

const AddModal = ({
  title,
  fields,
  onClose,
  onSave,
  enableExistingSelection = false,
  existingData = EMPTY_EXISTING_DATA,
  initialValues = EMPTY_INITIAL_VALUES,
  submitLabel = "Save",
}) => {
  const modalRef = useRef(null);
  const resolvedInitialValues = initialValues ?? EMPTY_INITIAL_VALUES;
  const resolvedExistingData = existingData ?? EMPTY_EXISTING_DATA;
  const initialValuesKey = useMemo(
    () => JSON.stringify(resolvedInitialValues),
    [resolvedInitialValues]
  );
  const [formData, setFormData] = React.useState(() =>
    buildInitialFormData(fields, resolvedInitialValues)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [useExisting, setUseExisting] = useState(false);
  const [selectedExistingId, setSelectedExistingId] = useState(null);

  useEffect(() => {
    setFormData(buildInitialFormData(fields, resolvedInitialValues));
    setUseExisting(false);
    setSelectedExistingId(null);
    setValidationError("");
  }, [title, initialValuesKey]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Handle form change
  const handleChange = (key, value, field) => {
    setFormData((prevData) => ({ ...prevData, [key]: value }));
  };

  // Handle file input change
  const handleFileChange = (key, file) => {
    setFormData((prevData) => ({ ...prevData, [key]: file }));
  };

  // Handle submit
  const handleSubmit = async () => {
    setValidationError("");
    
    if (useExisting) {
        if (!selectedExistingId) {
            setValidationError("Please select an existing contact.");
            return;
        }
        
        setIsSubmitting(true);
        try {
            await onSave({ existingUserId: selectedExistingId });
            onClose();
        } catch (error) {
            console.error('Error saving data:', error);
        } finally {
            setIsSubmitting(false);
        }
        return;
    }

    // Validate required fields
    const requiredFields = fields.filter(field => field.required);
    const missingFields = requiredFields.filter(field => {
      const value = formData[field.key];
      return !value || (typeof value === 'string' && value.trim() === '');
    });
    
    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(f => f.label).join(', ');
      setValidationError(`Please fill in all required fields: ${fieldNames}`);
      return;
    }

    const visibilityKeys = ["ShowInAtraq", "ShowInAUP", "ShowInAAP", "ShowOnWebsite"];
    const hasVisibilityFields = fields.some((field) => visibilityKeys.includes(field.key));
    if (hasVisibilityFields && !visibilityKeys.some((key) => formData[key] === true)) {
      setValidationError("Select at least one place to show this news.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSave(formData);
      // Only close if save was successful
      onClose();
    } catch (error) {
      console.error('Error saving data:', error);
      // Don't close modal on error, let the error toast show
      // The error is already handled in the parent component's onSave handler
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-3 sm:p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex-none flex justify-between items-start gap-3 p-4 sm:p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500 mt-1">Fill in the details below</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto min-h-0">
          {validationError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{validationError}</p>
            </div>
          )}

          {/* Toggle for Existing Contact */}
          {enableExistingSelection && (
             <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
               <label className="flex items-center space-x-3 cursor-pointer">
                 <input
                   type="checkbox"
                   checked={useExisting}
                   onChange={(e) => {
                     setUseExisting(e.target.checked);
                     setValidationError("");
                   }}
                   className="w-4 h-4 text-[#90c740] rounded cursor-pointer focus:ring-[#90c740]"
                 />
                 <span className="text-sm font-medium text-gray-700">Select an existing contact</span>
               </label>
             </div>
          )}

          {useExisting ? (
             <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Contact
                </label>
                <ContactSearchDropdown
                    contacts={resolvedExistingData}
                    value={selectedExistingId || ''}
                    onChange={(e) => setSelectedExistingId(e.target.value)}
                    placeholder="-- Select a Contact --"
                />
             </div>
          ) : (
             <>
                {/* Demo Password Notification - Show for contact creation, not company details */}
                {title && /(authorized|contact|user)/i.test(title) && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        </div>
                        <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">Demo Password Notice</p>
                        <p className="text-sm text-blue-700 mt-1">
                            A unique password will be generated when you save. You will see it once after create — share it with the user.
                        </p>
                        </div>
                    </div>
                    </div>
                )}
                <div className="space-y-6">
                    {fields.map((field) => (
                    <div key={field.key} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>

                        {/* Text Input */}
                        {(field.type === "text" || field.type === "email" || field.type === "date") && (
                        <input
                            type={field.type}
                            value={formData[field.key] || ""}
                            onChange={(e) => handleChange(field.key, e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#90c740] focus:border-[#90c740] transition-colors duration-200 placeholder-gray-400"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                        />
                        )}

                        {/* File Input */}
                        {field.type === "file" && (
                        <div className="relative">
                            <input
                            type="file"
                            onChange={(e) => handleFileChange(field.key, e.target.files[0])}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#90c740] focus:border-[#90c740] transition-colors duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#90c740] file:text-white hover:file:bg-[#7ebd2e] file:cursor-pointer"
                            />
                        </div>
                        )}

                        {/* Dropdown List */}
                        {field.type === "list" && Array.isArray(field.data) && (
                        <select
                            value={formData[field.key] || ""}
                            onChange={(e) => handleChange(field.key, e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#90c740] focus:border-[#90c740] transition-colors duration-200 bg-white"
                        >
                            <option value="" disabled>Select {field.label}</option>
                            {field.data.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.name || item.Name || `${item.Name || ''} ${item.Surname || ''}`.trim() || item.email || item.id}
                            </option>
                            ))}
                        </select>
                        )}

                        {/* Paragraph Input */}
                        {(field.type === "paragraph" || field.type === "textarea") && (
                        <textarea
                            value={formData[field.key] || ""}
                            onChange={(e) => handleChange(field.key, e.target.value)}
                            rows={field.key === "News" ? 6 : 4}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#90c740] focus:border-[#90c740] transition-colors duration-200 placeholder-gray-400 resize-none"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                        />
                        )}

                        {field.type === "checkbox" && (
                        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                            <input
                                type="checkbox"
                                checked={!!formData[field.key]}
                                onChange={(e) => handleChange(field.key, e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-[#90c740] focus:ring-[#90c740]"
                            />
                            <span className="text-sm text-gray-700">{field.label}</span>
                        </label>
                        )}
                    </div>
                    ))}
                </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-none flex flex-col-reverse gap-3 p-4 sm:flex-row sm:justify-end sm:gap-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full justify-center px-6 py-3 bg-[#90c740] text-white rounded-lg hover:bg-[#7ebd2e] transition-colors duration-200 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              useExisting ? 'Add Selected User' : submitLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddModal;